import React, { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, View } from "react-big-calendar";

import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import endOfMonth from "date-fns/endOfMonth";
import isSameDay from "date-fns/isSameDay";
import isWithinInterval from "date-fns/isWithinInterval";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Chip,
  Popover,
  Avatar,
  AvatarGroup,
  Tooltip,
  Button,
} from "@mui/material";
import enUS from "date-fns/locale/en-US";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TodayIcon from "@mui/icons-material/Today";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import SprintStartModal from "./SprintStartModal";
import { useBoardFilters } from "../custom-hooks/useBoardFilters";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  selectDebouncedSearchQuery,
  selectPagination,
  selectSelectedItem,
} from "@/features/bughurd/boardSelector";
import { useLazyGetListOfBugsQuery } from "@/features/bughurd/api";
import { useBoardInitialization } from "../custom-hooks/useBoardInitialization";
import { useParams } from "react-router-dom";
import TaskUpdateDrawer from "../../bugherdComponents/TaskUpdateDrawer";
import { useTaskOperations } from "../custom-hooks/useTaskOperations";
import TaskCreateModal from "../../bugherdComponents/TaskCreationModal";
import TaskCreateDrawer from "../../bugherdComponents/TaskCreateDrawer";
import {
  setCreateTask,
  setDueDateDateForCalendarView,
} from "@/features/bughurd/slice";

const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface Sprint {
  _id: string;
  name: string;
  status: "DRAFT" | "ACTIVE" | "ARCHIVE";
  startDate?: string;
  endDate?: string;
  totalBugCount?: number;
}

interface Task {
  _id: string;
  summary: string;
  bugKey: string;
  currentSprint?: {
    _id: string;
    name: string;
  };
  assignTo: Array<{
    _id: string;
    fullName: string;
    avatar?: string;
  }>;
  bugStatus?: {
    status: string;
  };
  dueDate?: string;
  startDate?: string;
  createdBy?: {
    fullName: string;
    avatar?: string;
  };
}

interface SprintTasksData {
  [sprintId: string]: {
    status: string;
    data: {
      decryptedData: {
        total: number;
        items: Task[];
      };
      encryptData: string;
    };
  };
}

interface Props {
  sprints: Sprint[];
  onSprintClick?: (sprint: Sprint) => void;
  onTaskClick?: (task: Task) => void;
  onDateClick?: (date: Date) => void;
  onPlusClick?: (date: Date) => void;
}

function getMonthWeeks(selected: Date) {
  const first = startOfWeek(startOfMonth(selected), { weekStartsOn: 1 });
  const last = endOfMonth(selected);
  const weeks: { start: Date; end: Date }[] = [];
  let current = first;
  while (current <= last) {
    weeks.push({ start: current, end: addDays(current, 6) });
    current = addDays(current, 7);
  }
  return weeks;
}

const CustomCalendarHeader = () => {
  const theme = useTheme();
  const weekArray = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {weekArray.map((day, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              textAlign: "center",
              fontWeight: "bold",
              color: theme.palette.text.secondary,
              py: 1,
            }}
          >
            {day}
          </Box>
        ))}
      </Box>
    </>
  );
};

const SprintsCalendarView: React.FC<Props> = ({
  sprints,
  onSprintClick,
  onTaskClick,
  onDateClick,
  onPlusClick,
}) => {
  const { id } = useParams<{ id: string }>();
  const { filters } = useBoardFilters();
  const dispatch = useAppDispatch();
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = filters;
  const pagination = useAppSelector(selectPagination);
  const {
    isMyTaskView,
    statusList,
    projectDetails,
    listOfBugs,
    tagList,
    companyMemberData,
    projects,
    currentUser,
  } = useBoardInitialization(id);
  const selectedItem = useAppSelector(selectSelectedItem);
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const theme = useTheme();
  const [date, setDate] = useState(new Date());
  const [hoveredSprintId, setHoveredSprintId] = useState<string | null>(null);
  const [selectedSprint, setSelectedSprint] = useState<Sprint | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [allMonthBugs, setAllMonthBugs] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createView, setCreateView] = useState(false);
  const [detailView, setDetailView] = useState(false);
  const [createData, setCreateData] = useState({
    dueDate: null,
    sprintId: "",
  });
  const [selectedAnchorEl, setSelectedAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const {
    handleTaskUpdate,
    handleLoadMore,
    handlePageChange,
    refreshColumnData,
  } = useTaskOperations(id, isMyTaskView);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);
  const [isDeleteSprintModalOpen, setIsDeleteSprintModalOpen] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState<Sprint | null>(null);
  const [trigger] = useLazyGetListOfBugsQuery();
  const weeks = useMemo(() => getMonthWeeks(date), [date]);
  const [morePopoverAnchor, setMorePopoverAnchor] =
    useState<HTMLElement | null>(null);
  const [morePopoverContent, setMorePopoverContent] = useState<{
    tasks: Task[];
    date: Date;
  } | null>(null);

  const getFilteredProjects = useMemo(() => {
    return () => {
      if (!isMyTaskView) return undefined;

      if (selectedProjects && selectedProjects.length > 0) {
        return selectedProjects;
      }

      return projects?.map((item) => item._id);
    };
  }, [isMyTaskView, selectedProjects, projects]);

  const fetchAllWeeksData = async () => {
    setIsLoading(true);
    const merged: Task[] = [];
    await Promise.all(
      weeks.map(async (w) => {
        try {
          const res = await trigger(
            {
              id: isMyTaskView ? "" : id || "",
              forBoard: true,
              search: debouncedSearchQuery || "",
              assignTo: isMyTaskView
                ? [currentUser?._id]
                : selectedAvatars?.length
                ? selectedAvatars
                : undefined,
              tags: selectedTags?.length ? selectedTags : undefined,
              statuses: selectedStatuses?.length ? selectedStatuses : undefined,
              severities: selectedPriorities?.length
                ? selectedPriorities
                : undefined,
              page: pagination.global?.page || 1,
              limit: pagination.global?.limit || 50,
              dueDateStart: format(w.start, "yyyy-MM-dd"),
              dueDateEnd: format(w.end, "yyyy-MM-dd"),

              ...(updatedAtStart && { updatedAtStart }),
              ...(updatedAtEnd && { updatedAtEnd }),
              ...(isRecurring && { isRecurring }),
              ...(startDateStart && { startDateStart }),
              ...(startDateEnd && { startDateEnd }),
              ...(selectedItem === "Recurring Tasks" && {
                isRecurring: true,
              }),
              ...(isMyTaskView && { project: getFilteredProjects() }),
            },
            true
          ).unwrap();
          const items: Task[] = res?.data?.decryptedData?.items || [];
          items.forEach((t) => {
            if (!merged.some((m) => m._id === t._id)) merged.push(t);
          });
        } catch {}
      })
    );
    setAllMonthBugs(merged);
    setIsLoading(false);
  };

  useEffect(() => {
    let cancel = false;
    async function fetchAll() {
      await fetchAllWeeksData();
    }
    fetchAll();
    return () => {
      cancel = true;
    };
  }, [
    refreshKey,
    date,
    id,
    isMyTaskView,
    selectedAvatars,
    selectedTags,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    pagination,
    debouncedSearchQuery,
    isRecurring,
    selectedItem,
    currentUser,
    updatedAtStart,
    updatedAtEnd,
    trigger,
    weeks,
    getFilteredProjects,
  ]);

  const bugsByDate = useMemo(() => {
    const map: Record<string, Task[]> = {};
    allMonthBugs.forEach((bug) => {
      if (bug.dueDate) {
        const key = format(new Date(bug.dueDate), "yyyy-MM-dd");
        if (!map[key]) map[key] = [];
        map[key].push(bug);
      }
    });
    return map;
  }, [allMonthBugs]);

  const sprintTasksMap = useMemo(() => {
    const map: Record<string, Task[]> = {};
    allMonthBugs.forEach((bug) => {
      if (bug.currentSprint?._id && bug.dueDate) {
        if (!map[bug.currentSprint._id]) {
          map[bug.currentSprint._id] = [];
        }
        map[bug.currentSprint._id].push(bug);
      }
    });
    return map;
  }, [allMonthBugs]);

  const events = useMemo(
    () =>
      sprints
        .filter((s) => s.startDate && s.endDate)
        .map((s) => ({
          id: s._id,
          title: s.name,
          start: new Date(s.startDate!),
          end: new Date(s.endDate!),
          resource: s,
          allDay: true,
        })),
    [sprints]
  );

  const CustomMonthDateHeader: React.FC<any> = () => null;

  const EventRenderer = ({ event }: any) => {
    const sprint: Sprint = event.resource;
    return (
      <Box
        onMouseEnter={() => setHoveredSprintId(sprint._id)}
        onMouseLeave={() =>
          setHoveredSprintId((prev) => (prev === sprint._id ? null : prev))
        }
        onClick={(e) => {
          e.stopPropagation();
          setSelectedSprint(sprint);
          setSelectedAnchorEl(e.currentTarget as HTMLElement);
          setPopoverPosition({
            top: e.clientY,
            left: e.clientX,
          });
          if (onSprintClick) onSprintClick(sprint);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          px: 0.5,
          py: 0.25,
          cursor: "pointer",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={sprint.name}
      />
    );
  };

  const handleMoreClick = (e: React.MouseEvent, tasks: Task[], date: Date) => {
    e.stopPropagation();
    setMorePopoverAnchor(e.currentTarget as HTMLElement);
    setMorePopoverContent({ tasks, date });
    setPopoverPosition({
      top: e.clientY,
      left: e.clientX,
    });
  };

  const handleMorePopoverClose = () => {
    setMorePopoverAnchor(null);
    setMorePopoverContent(null);
  };

  // Calculate sprint position info for continuous highlighting
  const getSprintPositionInfo = useMemo(() => {
    const weekGrid: Date[][] = [];
    let current = startOfWeek(startOfMonth(date), { weekStartsOn: 0 });
    const monthEnd = endOfMonth(date);

    while (current <= monthEnd) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current = addDays(current, 1);
      }
      weekGrid.push(week);
      if (current > addDays(monthEnd, 7)) break;
    }

    const sprintPositions: Record<
      string,
      {
        segments: Array<{
          weekIndex: number;
          startDay: number;
          endDay: number;
          isStart: boolean;
          isEnd: boolean;
        }>;
      }
    > = {};

    sprints.forEach((sprint) => {
      if (!sprint.startDate || !sprint.endDate) return;

      const sprintStart = new Date(sprint.startDate);
      const sprintEnd = new Date(sprint.endDate);
      const segments: any[] = [];

      weekGrid.forEach((week, weekIndex) => {
        let segmentStart = -1;
        let segmentEnd = -1;

        week.forEach((day, dayIndex) => {
          if (
            isWithinInterval(day, { start: sprintStart, end: sprintEnd }) ||
            isSameDay(day, sprintStart) ||
            isSameDay(day, sprintEnd)
          ) {
            if (segmentStart === -1) segmentStart = dayIndex;
            segmentEnd = dayIndex;
          }
        });

        if (segmentStart !== -1) {
          segments.push({
            weekIndex,
            startDay: segmentStart,
            endDay: segmentEnd,
            isStart: isSameDay(week[segmentStart], sprintStart),
            isEnd: isSameDay(week[segmentEnd], sprintEnd),
          });
        }
      });

      sprintPositions[sprint._id] = { segments };
    });

    return { weekGrid, sprintPositions };
  }, [date, sprints]);

  const CustomDateCellWrapper: React.FC<any> = ({ value, children }) => {
    const [hover, setHover] = useState(false);

    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(setCreateTask(true));
      dispatch(setDueDateDateForCalendarView(value));
      if (onDateClick) onDateClick(value);
    };

    const dateKey = format(value, "yyyy-MM-dd");
    const dayBugs = bugsByDate[dateKey] || [];

    const handlePlusClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCreateView(true);
      setCreateData({
        dueDate: value,
        sprintId: "",
      });
      dispatch(setCreateTask(true));
      dispatch(setDueDateDateForCalendarView(value));
      if (onPlusClick) onPlusClick(value);
    };

    const dayNumber = format(value, "d");
    const hoveredSprint = hoveredSprintId
      ? sprints.find((s) => s._id === hoveredSprintId)
      : null;

    // Find all sprints that include this date
    const sprintsForDate = sprints.filter((sprint) => {
      if (!sprint.startDate || !sprint.endDate) return false;
      const start = new Date(sprint.startDate);
      const end = new Date(sprint.endDate);
      return (
        isWithinInterval(value, { start, end }) ||
        isSameDay(value, start) ||
        isSameDay(value, end)
      );
    });

    // Get all tasks for this date (both sprint and non-sprint)
    const allTasksForDate: Task[] = [];

    // Add sprint tasks that start on this date
    sprints
      .filter((sprint) => isSameDay(new Date(sprint.startDate || ""), value))
      .forEach((sprint) => {
        const sprintTasks = sprintTasksMap[sprint._id] || [];
        const tasksForThisDate = sprintTasks.filter((task) =>
          isSameDay(new Date(task.dueDate!), value)
        );
        allTasksForDate.push(...tasksForThisDate);
      });

    // Add non-sprint tasks for this date
    const nonSprintTasks = dayBugs.filter((bug) => {
      return (
        !bug.currentSprint ||
        !sprints.some(
          (s) =>
            s._id === bug.currentSprint?._id &&
            s.startDate &&
            isSameDay(new Date(s.startDate), value)
        )
      );
    });
    allTasksForDate.push(...nonSprintTasks);

    // Check if we need to show "more" button
    const showMoreButton = allTasksForDate.length > 2;
    const visibleTasks = showMoreButton
      ? allTasksForDate.slice(0, 2)
      : allTasksForDate;
    const moreCount = allTasksForDate.length - 2;

    return (
      <Box
        sx={{
          position: "relative",
          height: "100%",
          width: "100%",
          cursor: "pointer",
          transition: "background-color 0.12s ease-in-out",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleCellClick}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 6px",
            flex: "0 0 28px",
            position: "relative",
            zIndex: 10, // Ensure date number and plus button are above everything
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            {dayNumber}
          </Typography>
          {hover && (
            <IconButton
              size="small"
              onClick={handlePlusClick}
              sx={{
                padding: "2px",
                color: theme.palette.primary.main,
                backgroundColor: "#fff",
                borderRadius: 1,
                "&:hover": { backgroundColor: theme.palette.grey[300] },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Persistent sprint strip background */}
        {sprintsForDate.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 28,
              left: 0,
              right: 0,
              height: 24,
              backgroundColor: "rgba(66,139,202,0.08)",
              borderTop: "1px solid rgba(66,139,202,0.1)",
              borderBottom: "1px solid rgba(66,139,202,0.1)",
              zIndex: 3,
            }}
          />
        )}

        {/* Sprint names and tasks */}
        <Box sx={{ position: "relative", zIndex: 5 }}>
          {sprints
            .filter((sprint) =>
              isSameDay(new Date(sprint.startDate || ""), value)
            )
            .map((sprint) => {
              const sprintTasks = sprintTasksMap[sprint._id] || [];
              const isHovered = hoveredSprintId === sprint._id;

              return (
                <React.Fragment key={sprint._id}>
                  <Box
                    onMouseEnter={() => setHoveredSprintId(sprint._id)}
                    onMouseLeave={() =>
                      setHoveredSprintId((prev) =>
                        prev === sprint._id ? null : prev
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSprint(sprint);
                      setSelectedAnchorEl(e.currentTarget as HTMLElement);
                      setPopoverPosition({
                        top: e.clientY,
                        left: e.clientX,
                      });
                      if (onSprintClick) onSprintClick(sprint);
                    }}
                    sx={{
                      px: 0.5,
                      cursor: "pointer",
                      backgroundColor: isHovered
                        ? "rgba(66,139,202,0.1)"
                        : "transparent",
                      borderRadius: "4px",
                      position: "relative",
                      zIndex: 6,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.85rem",
                        lineHeight: 1.1,
                      }}
                      noWrap
                    >
                      {sprint.name}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })}

          {/* Render all visible tasks */}
          {visibleTasks.map((task) => (
            <Box
              key={task._id}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
                setSelectedTask(task);
                if (onTaskClick) onTaskClick(task);
              }}
              sx={{
                mb: 0.5,
                backgroundColor: theme.palette.grey[100],
                borderRadius: 1,
                cursor: "pointer",
                width: "100%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                zIndex: 4, // Lower than hovered sprint strip
                padding: "2px 4px",
                "&:hover": {
                  backgroundColor: theme.palette.grey[200],
                  zIndex: 8, // Higher when hovered, but still below hovered sprint
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <span
                  style={{ color: theme.palette.primary.main, flexShrink: 0 }}
                >
                  {task.bugKey}:
                </span>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    display: "inline-block",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {task.summary.length > 10 ? (
                    <Tooltip title={task.summary}>
                      <span>{task.summary.slice(0, 10)}...</span>
                    </Tooltip>
                  ) : (
                    task.summary
                  )}
                </span>
              </Typography>
            </Box>
          ))}
        </Box>

        {showMoreButton && (
          <Typography
            onClick={(e) => handleMoreClick(e, allTasksForDate, value)}
            sx={{
              fontSize: "0.7rem",
              color: theme.palette.text.secondary,
              cursor: "pointer",
              position: "relative",
              zIndex: 4,
              "&:hover": {
                color: theme.palette.primary.main,
                zIndex: 8,
              },
            }}
          >
            {`+${moreCount} more`}
          </Typography>
        )}

        <Box sx={{ height: "calc(100% - 28px)", overflow: "hidden" }}>
          {children}
        </Box>
      </Box>
    );
  };

  // Sprint highlight overlay component
  const SprintHighlightOverlay = () => {
    if (!hoveredSprintId) return null;

    const { weekGrid, sprintPositions } = getSprintPositionInfo;
    const positions = sprintPositions[hoveredSprintId];

    if (!positions) return null;

    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 15, // Higher than everything except date headers
        }}
      >
        {positions.segments.map((segment, index) => {
          const weekHeight = 100 / weekGrid.length; // Percentage height per week
          const dayWidth = 100 / 7; // Percentage width per day

          return (
            <Box
              key={`${hoveredSprintId}-${index}`}
              sx={{
                position: "absolute",
                top: `${segment.weekIndex * weekHeight}%`,
                left: `${segment.startDay * dayWidth}%`,
                width: `${(segment.endDay - segment.startDay + 1) * dayWidth}%`,
                height: 24, // Fixed height for sprint strip only
                backgroundColor: "rgba(66,139,202,0.3)",
                border: "2px solid rgba(66,139,202,0.5)",
                marginTop: "28px", // Account for date header
                boxSizing: "border-box",
              }}
            />
          );
        })}
      </Box>
    );
  };

  const SprintPopover: React.FC = () => {
    if (!selectedSprint) return null;

    const open = Boolean(selectedAnchorEl);
    const sprintTasks = selectedSprint
      ? sprintTasksMap[selectedSprint._id] || []
      : [];

    const handleClose = () => {
      setSelectedSprint(null);
      setSelectedAnchorEl(null);
      setMenuAnchor(null);
    };

    const handleMenuClose = () => {
      setMenuAnchor(null);
    };

    return (
      <>
        <Popover
          open={open}
          anchorReference="anchorPosition"
          anchorPosition={popoverPosition}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              p: 2,
              width: 320,
              borderRadius: "8px",
              maxHeight: 400,
              overflow: "auto",
              position: "relative",
            },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {selectedSprint.name}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Chip
              label={selectedSprint.status}
              size="small"
              sx={{
                mt: 1,
                backgroundColor:
                  selectedSprint.status === "ACTIVE"
                    ? theme.palette.success.light
                    : selectedSprint.status === "DRAFT"
                    ? theme.palette.warning.light
                    : theme.palette.grey[300],
              }}
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Start date
                </Typography>
                <Typography variant="body2">
                  {selectedSprint.startDate
                    ? format(new Date(selectedSprint.startDate), "MMM d, yyyy")
                    : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Planned end
                </Typography>
                <Typography variant="body2">
                  {selectedSprint.endDate
                    ? format(new Date(selectedSprint.endDate), "MMM d, yyyy")
                    : "-"}
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
              Tasks ({sprintTasks.length})
            </Typography>

            <Box sx={{ mt: 1 }}>
              {sprintTasks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No tasks in this sprint
                </Typography>
              ) : (
                sprintTasks.map((task) => (
                  <Box
                    key={task._id}
                    onClick={() => {
                      setSelectedTask(task);
                      setIsModalOpen(true);
                      if (onTaskClick) onTaskClick(task);
                      handleClose();
                    }}
                    sx={{
                      p: 1,
                      mb: 1,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.grey[100],
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <span style={{ color: theme.palette.primary.main }}>
                        {task.bugKey}:
                      </span>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {task.summary}
                      </span>
                    </Typography>
                    {task.assignTo.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <AvatarGroup max={3} sx={{ "--Avatar-size": "20px" }}>
                          {task.assignTo.map((user) => (
                            <Tooltip key={user._id} title={user.fullName}>
                              <Avatar
                                alt={user.fullName}
                                src={user.avatar}
                                sx={{ width: 20, height: 20 }}
                              />
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
          <Box>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              disablePortal={false}
              sx={{
                zIndex: theme.zIndex.modal + 1,
              }}
            >
              <MenuItem
                onClick={() => {
                  setIsEditSprintModalOpen(true);
                  handleMenuClose();
                }}
              >
                Edit Sprint
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSprintToDelete(selectedSprint);
                  setIsDeleteSprintModalOpen(true);
                  handleMenuClose();
                }}
                sx={{ color: theme.palette.error.main }}
              >
                Delete Sprint
              </MenuItem>
            </Menu>
          </Box>
        </Popover>
      </>
    );
  };

  const MoreTasksPopover: React.FC = () => {
    if (!morePopoverContent) return null;
    const tasksForDate = morePopoverContent.tasks.filter(
      (task) =>
        task.dueDate &&
        isSameDay(new Date(task.dueDate), morePopoverContent.date)
    );
    return (
      <Popover
        open={Boolean(morePopoverAnchor)}
        anchorEl={morePopoverAnchor}
        anchorReference="anchorPosition"
        anchorPosition={popoverPosition}
        onClose={handleMorePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 320,
            borderRadius: "8px",
            maxHeight: 400,
            overflow: "auto",
            zIndex: theme.zIndex.modal + 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Tasks for {format(morePopoverContent.date, "MMM d, yyyy")}
          </Typography>
          <IconButton
            size="small"
            onClick={handleMorePopoverClose}
            sx={{ p: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {tasksForDate?.map((task) => (
            <Box
              key={task._id}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
                setSelectedTask(task);
                handleMorePopoverClose();
                if (onTaskClick) onTaskClick(task);
              }}
              sx={{
                p: 1,
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span style={{ color: theme.palette.primary.main }}>
                  {task.bugKey}:
                </span>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {task.summary}
                </span>
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    );
  };

  const eventStyleGetter = () => ({
    style: {
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
      border: "none",
      boxShadow: "none",
      padding: 0,
    },
  });

  return (
    <>
      <Paper
        key={refreshKey}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          height: "calc(100vh - 250px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 2,
            gap: 1,
          }}
        >
          <IconButton
            onClick={() =>
              setDate(new Date(date.getFullYear(), date.getMonth() - 1, 1))
            }
          >
            <ArrowBackIcon />
          </IconButton>
          <TextField
            type="month"
            size="small"
            value={format(date, "yyyy-MM")}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-");
              if (y && m) {
                const selectedDate = new Date(Number(y), Number(m) - 1, 1);
                if (!isNaN(selectedDate.getTime())) setDate(selectedDate);
              }
            }}
            sx={{ width: 140 }}
          />
          <IconButton
            onClick={() =>
              setDate(new Date(date.getFullYear(), date.getMonth() + 1, 1))
            }
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
        <CustomCalendarHeader />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            position: "relative",
            height: "100%",
            minHeight: "300px",
            maxHeight: "100%",
          }}
        >
          <Box sx={{ position: "relative", height: "auto", minHeight: 550 }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "auto", minHeight: 550 }}
              views={["month"] as View[]}
              defaultView="month"
              date={date}
              onNavigate={(newDate) => setDate(newDate)}
              components={{
                toolbar: () => null,
                month: {
                  dateHeader: CustomMonthDateHeader,
                  header: () => null,
                },
                dateCellWrapper: CustomDateCellWrapper,
                event: EventRenderer,
              }}
              eventPropGetter={eventStyleGetter}
            />
            <SprintHighlightOverlay />
          </Box>
          <SprintPopover />
          <MoreTasksPopover />
        </Box>

        {/* Add the modals here */}
        {selectedSprint && (
          <SprintStartModal
            open={isEditSprintModalOpen}
            onClose={() => setIsEditSprintModalOpen(false)}
            onStart={(data) => {
              // Handle sprint update here
              setIsEditSprintModalOpen(false);
            }}
            sprint={selectedSprint}
            taskCount={sprintTasksMap[selectedSprint._id]?.length || 0}
            isEditMode={true}
          />
        )}

        <DeleteConfirmModal
          open={isDeleteSprintModalOpen}
          onClose={() => setIsDeleteSprintModalOpen(false)}
          onConfirm={() => {
            // Handle sprint deletion here
            setIsDeleteSprintModalOpen(false);
            setSelectedSprint(null);
            setSelectedAnchorEl(null);
          }}
          loading={false}
          title="Delete Sprint"
          confirmMessage={
            sprintToDelete
              ? `Are you sure you want to delete sprint '${sprintToDelete.name}'? This action cannot be undone.`
              : "Are you sure you want to delete this sprint?"
          }
          okText="Delete"
        />
      </Paper>
      {isModalOpen && (
        <TaskUpdateDrawer
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={selectedTask?._id}
          users={companyMemberData || []}
          createdBy={selectedTask?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          refreshColumn={() => {
            refreshColumnData();
            fetchAllWeeksData();
            setRefreshKey((prev) => prev + 1);
          }}
          onTaskUpdate={handleTaskUpdate}
          task={selectedTask}
        />
      )}
    </>
  );
};

export default SprintsCalendarView;
